import type { JobsiteBusinessContext } from "../types/runtime-config";
import { primaryMarketLabel } from "@servicepro/utils";

/**
 * One line of a business's facts card: something the owner told us that is true.
 * Only facts with data behind them become lines; there is no default list.
 */
interface BusinessFact {
  /** Which fact this is, so a renderer can pick its icon. */
  kind: "licensed" | "insured" | "rating" | "years" | "emergency";
  text: string;
  /** A smaller second line: the license number, the review count. */
  detail?: string;
}

const
  /** Below this a Google rating argues against the business, so the card leaves it out. */
  MIN_RATING_SHOWN = 4,
  /** How many town names the hero's label lists before it would wrap on a phone. */
  MAX_LABEL_TOWNS = 3,
  MAX_LABEL_CHARACTERS = 32,

  /**
   * "License #123456" for a bare code, the owner's own wording otherwise ("CSLB 123456").
   * @param {string | null | undefined} licenseNumber The number as the owner typed it.
   * @returns {string | undefined} The detail line, or undefined when there is no number.
   */
  licenseDetail = (licenseNumber: string | null | undefined): string | undefined => {
    const number = licenseNumber?.trim() ?? "";
    if (number === "") {
      return undefined;
    }
    if (/^[A-Z]*\d[\dA-Z-]*$/iu.test(number)) {
      return `License #${number}`;
    }
    return `License ${number}`;
  },

  /**
   * "43 reviews", or "1 review".
   * @param {number | null | undefined} count The review count Google reports.
   * @returns {string | undefined} The detail line, or undefined without a count.
   */
  reviewCountDetail = (count: number | null | undefined): string | undefined => {
    if (count === null || count === undefined || count < 1) {
      return undefined;
    }
    if (count === 1) {
      return "1 review";
    }
    return `${count.toLocaleString("en-US")} reviews`;
  },

  /**
   * The credentials the owner confirmed: licensed (with the number) and insured.
   * A stored false or null is never a line: null means nobody asked.
   * @param {JobsiteBusinessContext} business The business.
   * @returns {BusinessFact[]} Zero to two lines.
   */
  credentialFacts = (business: JobsiteBusinessContext): BusinessFact[] => [
    ...(business.isLicensed === true
      ? [{ detail: licenseDetail(business.licenseNumber), kind: "licensed" as const, text: "Licensed" }]
      : []),
    ...(business.isInsured === true ? [{ kind: "insured" as const, text: "Insured" }] : []),
  ],

  /**
   * The Google rating, when it helps the business: 4 stars and up.
   * @param {JobsiteBusinessContext} business The business.
   * @returns {BusinessFact[]} Zero or one line.
   */
  ratingFacts = (business: JobsiteBusinessContext): BusinessFact[] => {
    const rating = business.googleRating ?? null;
    if (rating === null || rating < MIN_RATING_SHOWN) {
      return [];
    }
    return [{ detail: reviewCountDetail(business.googleReviewCount), kind: "rating", text: `${rating.toFixed(1)} stars on Google` }];
  },

  /**
   * Years in business as a floor: the count was saved in some past year and only grows, so "N+" stays true.
   * @param {JobsiteBusinessContext} business The business.
   * @returns {BusinessFact[]} Zero or one line.
   */
  yearsFacts = (business: JobsiteBusinessContext): BusinessFact[] => {
    const years = business.yearsInBusiness ?? null;
    if (years === null || years < 1) {
      return [];
    }
    return [{ kind: "years", text: `${years}+ ${years === 1 ? "year" : "years"} in business` }];
  },

  /**
   * The facts a business has told us, in the order a customer weighs them.
   * @param {JobsiteBusinessContext} [business] The business in Puck's metadata.
   * @returns {BusinessFact[]} The lines, possibly none.
   */
  businessFacts = (business?: JobsiteBusinessContext): BusinessFact[] => {
    if (!business) {
      return [];
    }
    return [
      ...credentialFacts(business),
      ...ratingFacts(business),
      ...yearsFacts(business),
      ...(business.emergencyService === true ? [{ kind: "emergency" as const, text: "Emergency service" }] : []),
    ];
  },

  /**
   * The business's town names, primary first, each once.
   * @param {JobsiteBusinessContext} [business] The business.
   * @returns {{ name: string; state?: string }[]} The towns.
   */
  townsOf = (business?: JobsiteBusinessContext): { name: string; state?: string }[] => {
    const areas = business?.serviceAreas ?? [],
      primaryFirst = [...areas.filter((area) => area.isPrimary === true), ...areas.filter((area) => area.isPrimary !== true)],
      seen = new Set<string>();
    return primaryFirst.flatMap((area) => {
      const name = area.name?.trim() ?? "";
      if (name === "" || seen.has(name)) {
        return [];
      }
      seen.add(name);
      return [{ name, state: area.state }];
    });
  },

  /**
   * The leading town names that fit one line on a phone, never fewer than one.
   * @param {string[]} names The town names, primary first.
   * @returns {string[]} The names that fit, in order.
   */
  namesThatFit = (names: string[]): string[] => {
    const picked: string[] = [];
    for (const name of names) {
      const isFull = picked.length === MAX_LABEL_TOWNS || [...picked, name].join(" · ").length > MAX_LABEL_CHARACTERS;
      if (picked.length > 0 && isFull) {
        break;
      }
      picked.push(name);
    }
    return picked;
  },

  /**
   * Where the business works, short enough for one line on a phone:
   * "Rochester · Greece · Webster", or "Rochester, NY" for one town.
   * Falls back to the business's own town, then to "".
   * @param {JobsiteBusinessContext} [business] The business in Puck's metadata.
   * @returns {string} The label, or "" when the business names no town.
   */
  serviceAreaLabel = (business?: JobsiteBusinessContext): string => {
    const towns = townsOf(business);
    if (towns.length === 1) {
      return [towns[0].name, towns[0].state].filter(Boolean).join(", ");
    }
    if (towns.length > 1) {
      return namesThatFit(towns.map((town) => town.name)).join(" · ");
    }
    return primaryMarketLabel(business?.primaryMarket);
  },

  /**
   * Town names in a sentence a customer reads: "Rochester, Greece, and Webster".
   * @param {JobsiteBusinessContext} [business] The business in Puck's metadata.
   * @returns {string} The list with an Oxford comma, or "" without towns.
   */
  serviceAreaSentenceList = (business?: JobsiteBusinessContext): string => {
    const names = townsOf(business).map((town) => town.name);
    if (names.length <= 2) {
      return names.join(" and ");
    }
    // Not names.at(-1): Safari before 15.4 has no Array.prototype.at, and every contact form calls this.
    const [last] = names.slice(-1);
    return `${names.slice(0, -1).join(", ")}, and ${last}`;
  };

export type { BusinessFact };
export { businessFacts, serviceAreaLabel, serviceAreaSentenceList };
